import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const JournalNeuroArts = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Gallery images with IDs for navigation
  const galleryImages = [
    { id: 1, url: '/images/journal/NeuroArts1.png', caption: '', description: '' },
    { id: 2, url: '/images/journal/NeuroArts2.jpg', caption: '', description: '' },
  ];

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage]);

  // Touch/swipe navigation
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const goToPrevious = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : galleryImages.length - 1;
    setSelectedImage(galleryImages[previousIndex]);
  };

  const goToNext = () => {
    const currentIndex = galleryImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = currentIndex < galleryImages.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(galleryImages[nextIndex]);
  };

  const neuroArtsContent = {
    title: 'NeuroArts',
    researchArea: 'Neurodivergence & Creative Practice',
    abstract: 'Exploring the intersection of neurodivergence and artistic expression in the Kenyan creative sector.',
    body: [
      'NeuroArts is an emerging research initiative at Ziraya Institute that investigates how neurodivergent creatives experience, navigate, and transform artistic practice. The term "NeuroArts" describes the unique creative expressions, processes, and innovations that emerge from neurodivergent ways of thinking, perceiving, and being in the world.',
      'This research stream recognizes that neurodivergent artists often develop distinctive creative methodologies—whether through heightened sensory awareness, pattern recognition, deep focus, or associative thinking. These cognitive differences are not deficits but rather alternative frameworks for artistic creation that can lead to groundbreaking work.',
      'Through interviews, studio observations, and collaborative workshops, NeuroArts documents how neurodivergent creatives in Kenya approach their craft, navigate creative industries, and advocate for more inclusive workspaces. The research aims to move beyond accommodation toward celebration of neurodivergent creative contributions.'
    ],
    keyQuestions: [
      'How does neurodivergence shape artistic process and creative output?',
      'What barriers do neurodivergent creatives face in Kenya\'s creative industries?',
      'How can creative spaces be designed to support diverse cognitive styles?',
      'What can the broader creative sector learn from neurodivergent ways of creating?',
      'How do neurodivergent artists navigate collaboration and communication in creative teams?'
    ],
  };

  return (
    <>
      <section className="journal-entry-header">
        <div className="container">
          <div className="entry-meta-top">
            <span className="research-tag">{neuroArtsContent.researchArea}</span>
          </div>
          <h1>{neuroArtsContent.title}</h1>
          <p className="entry-abstract">{neuroArtsContent.abstract}</p>
        </div>
      </section>

      <section className="journal-entry-content wide-container">
        <div className="container">
          <div className="entry-body">
            {neuroArtsContent.body.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          {/* SPCTRM Open Studio Sessions Link */}
          <div className="related-project highlight-box">
            <h2>Participate in SPCTRM Open Studio Sessions</h2>
            <p>As part of the NeuroArts research initiative, we invite neurodivergent creatives to join our monthly Open Studio Sessions—a space for conversation, connection, and community.</p>
            <Link to="/project/spectrum-creatives" className="btn">Learn More & Register</Link>
          </div>

          {/* Key Questions */}
          <div className="key-questions">
            <h2>Key Questions</h2>
            <ul>
              {neuroArtsContent.keyQuestions.map((question, index) => (
                <li key={index}>{question}</li>
              ))}
            </ul>
          </div>

          {/* Practical Resources Link */}
          <div className="resources-preview">
            <h2>Resources for Neurodivergent Creatives</h2>
            <p>Explore practical tools, techniques, and supports for neurodivergent artists.</p>
            <Link to="/project/spectrum-creatives#resources" className="text-link">View Resources →</Link>
          </div>

          {/* Gallery Section  */}
          {galleryImages.length > 0 && (
            <div className="journal-gallery">
              <h2>Research Gallery</h2>
              <p className="gallery-subtitle"></p>
              <div className="gallery-grid">
                {galleryImages.map((image) => (
                  <div 
                    key={image.id} 
                    className="gallery-item"
                    onClick={() => openLightbox(image)}
                  >
                    <div className="gallery-image-wrapper">
                      <img 
                        src={image.url} 
                        alt={image.caption} 
                        className="gallery-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/placeholder.jpg';
                        }}
                      />
                    </div>
                    {image.caption && (
                      <div className="gallery-caption">
                        <h4>{image.caption}</h4>
                        {image.description && <p>{image.description}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Collaboration CTA */}
          <div className="entry-collaboration">
            <p className="collaboration-note">
              NeuroArts is an ongoing research initiative. If you're a neurodivergent creative 
              interested in sharing your experiences, or a researcher wanting to collaborate,
               please reach out: <a href="mailto:projects@zirayainstitute.org">projects@zirayainstitute.org</a>
            </p>
          </div>

          <div className="back-to-journal">
            <Link to="/research-journal" className="text-link">← Back to Research Journal</Link>
          </div>
        </div>
      </section>

      {/* Enhanced Lightbox Modal with Navigation - Same as Msichana wa AI */}
      {selectedImage && (
        <div 
          className="lightbox-modal" 
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="fas fa-times"></i>
            </button>
            
            {/* Previous Button */}
            <button className="lightbox-nav prev" onClick={goToPrevious}>
              <i className="fas fa-chevron-left"></i>
            </button>
            
            {/* Next Button */}
            <button className="lightbox-nav next" onClick={goToNext}>
              <i className="fas fa-chevron-right"></i>
            </button>
            
            <div className="lightbox-image-wrapper">
              <img 
                src={selectedImage.url} 
                alt={selectedImage.caption} 
                className="lightbox-image"
              />
            </div>
            
            {/* Image counter */}
            <div className="lightbox-counter">
              {galleryImages.findIndex(img => img.id === selectedImage.id) + 1} / {galleryImages.length}
            </div>
            
            {(selectedImage.caption || selectedImage.description) && (
              <div className="lightbox-caption">
                <h3>{selectedImage.caption}</h3>
                <p>{selectedImage.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default JournalNeuroArts;